import React, { useState } from "react";
import api from "@/lib/api";
import { REACTIONS, getGuestId } from "@/lib/guest";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Reaction bar: horizontal pill row of emoji + count. Tap toggles.
 * variant: "full" (labels + big emojis) | "compact" (small pills, no labels)
 */
export default function ReactionBar({ slug, photo, initialCounts, initialMine, onUpdate, variant = "full" }) {
  const [counts, setCounts] = useState(initialCounts || photo?.reactions || {});
  const [mine, setMine] = useState(new Set(initialMine || []));
  const [busy, setBusy] = useState(null);
  const [burst, setBurst] = useState(null); // key that just fired for burst animation

  const toggle = async (emojiKey) => {
    if (busy) return;
    setBusy(emojiKey);
    // Optimistic
    const hadIt = mine.has(emojiKey);
    const nextCounts = { ...counts };
    nextCounts[emojiKey] = Math.max(0, (nextCounts[emojiKey] || 0) + (hadIt ? -1 : 1));
    const nextMine = new Set(mine);
    if (hadIt) nextMine.delete(emojiKey);
    else nextMine.add(emojiKey);
    setCounts(nextCounts);
    setMine(nextMine);
    if (!hadIt) {
      setBurst(emojiKey);
      setTimeout(() => setBurst(null), 700);
    }

    try {
      const { data } = await api.post(
        `/public/events/${slug}/photos/${photo.id}/react`,
        { emoji: emojiKey, guest_id: getGuestId() },
      );
      setCounts(data.counts || {});
      setMine(new Set(data.my_reactions || []));
      if (onUpdate) onUpdate({ counts: data.counts, mine: data.my_reactions });
    } catch {
      // rollback
      setCounts(counts);
      setMine(mine);
    } finally {
      setBusy(null);
    }
  };

  const size = variant === "compact" ? "small" : "full";

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-2 ${size === "small" ? "text-sm" : ""}`}
      data-testid={`reaction-bar-${photo.id}`}
    >
      {REACTIONS.map((r) => {
        const active = mine.has(r.key);
        const count = counts[r.key] || 0;
        return (
          <button
            key={r.key}
            onClick={(e) => {
              e.stopPropagation();
              toggle(r.key);
            }}
            disabled={busy === r.key}
            className={`relative rounded-full flex items-center gap-1.5 transition-all ${
              size === "small" ? "px-2.5 py-1" : "px-4 py-2"
            } ${
              active
                ? "bg-[#FF007F] text-white shadow-[0_0_18px_rgba(255,0,127,0.5)]"
                : "bg-white/10 text-white/80 hover:bg-white/15"
            }`}
            data-testid={`react-${r.key}-${photo.id}`}
            title={r.label}
          >
            <span className={size === "small" ? "text-base" : "text-lg"}>
              {r.char}
            </span>
            {count > 0 && (
              <span className={`font-bold ${size === "small" ? "text-xs" : "text-sm"}`}>
                {count}
              </span>
            )}
            <AnimatePresence>
              {burst === r.key && (
                <motion.span
                  initial={{ opacity: 1, y: 0, scale: 1 }}
                  animate={{ opacity: 0, y: -40, scale: 1.8 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7 }}
                  className="absolute left-1/2 -translate-x-1/2 pointer-events-none text-2xl"
                >
                  {r.char}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        );
      })}
    </div>
  );
}
